/**
 * @fileoverview Redirect component that displays a redirect message and a spinner.
 * It also handles the state of the redirect and displays different messages based on the state.
 *
 * @example
 * // Basic usage
 * import { Redirect } from './Redirect'
 *
 * function TestRedirect() {
 *   return <Redirect question={...} />
 * }
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import config from '../config'
import { Icon } from './Icon'
import { NavigationButton } from './NavigationButton'
import css from './Redirect.module.css'
import { Text } from './typography/Typography'

/**
 * @enum {string} State
 * @description The state of the redirect.
 * @property {string} Processing - The redirect is processing.
 * @property {string} Redirecting - The redirect is redirecting.
 * @property {string} Halt - The redirect is halted.
 * @property {string} Fail - The redirect has failed.
 */
enum State {
    Processing,
    Redirecting,
    Halt,
    Fail,
}

/**
 * @function Redirect
 * @description Displays a redirect message and a spinner.
 * @param {Novoic.RedirectQuestionProps} props - The props for the Redirect component.
 * @returns {JSX.Element} The rendered Redirect component.
 */
export function Redirect(props: Novoic.RedirectQuestionProps) {
    const { t } = useTranslation()
    const [state, setState] = useState<State>(State.Processing)
    const { question } = props

    useEffect(() => {
        if (state !== State.Processing) return
        setTimeout(() => {
            window.location.href = question.question.location
            setState(State.Redirecting)
        }, 1000)
    }, [state])

    useEffect(() => {
        if (state !== State.Redirecting) return
        setTimeout(() => {
            setState(State.Halt)
        }, 5000)
    }, [state])

    useEffect(() => {
        if (state !== State.Halt) return
        setTimeout(() => {
            setState(State.Fail)
        }, 5000)
    }, [state])

    return (
        <div className={css.container}>
            {(state === State.Processing || state === State.Redirecting) && (
                <>
                    <div className={css.content}>
                        <img
                            alt="progress"
                            className={css.spinner}
                            src={config.s3.getStaticURL('loader.svg')}
                        />
                    </div>
                    <h1 className={css.header}>{t('screens.redirect.processing')}</h1>
                </>
            )}

            {state === State.Halt && (
                <>
                    <div className={css.content}>
                        <img
                            alt="progress"
                            className={css.spinner}
                            src={config.s3.getStaticURL('loader.svg')}
                        />
                    </div>
                    <h1 className={css.header}>{t('screens.redirect.halt')}</h1>
                </>
            )}

            {state === State.Fail && (
                <>
                    <Icon name="hourglass" />
                    <h1 className={css.header}>{t('screens.redirect.tooLong')}</h1>
                    <Text className={css.description}>{t('screens.redirect.description')}</Text>
                    <NavigationButton
                        onClick={() => {
                            window.location.href = question.question.location
                            return new Promise(() => {
                                // eslint-disable-next-line @typescript-eslint/no-empty-function
                            })
                        }}
                        label={t('buttons.next')}
                        type={'confirm'}
                    />
                </>
            )}
        </div>
    )
}
